import { Link } from 'react-router-dom';
import { ROUTES } from '@/app/router/routes';
import type { ResolvedDashboardSetting } from '../utils/resolveDashboardSetting';

export interface DashboardSettingSourceNoteProps {
  resolved: ResolvedDashboardSetting;
  className?: string;
}

export function DashboardSettingSourceNote({ resolved, className = '' }: DashboardSettingSourceNoteProps) {
  const message =
    resolved.source === 'settings'
      ? `Using ${resolved.displayValue} from Settings (${resolved.name}).`
      : `Using hard-coded default ${resolved.displayValue} (${resolved.name} not defined in Settings).`;

  return (
    <p className={`text-xs text-slate-500 ${className}`.trim()}>
      {message}{' '}
      <Link to={ROUTES.settings} className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
        Change in Settings
      </Link>
    </p>
  );
}

export interface DashboardSettingSourceNotesProps {
  resolved: ResolvedDashboardSetting[];
  className?: string;
  /** One sentence + single Settings link (Teacher Equity severity block). */
  singleLink?: boolean;
  /** Short labels in order, matching `resolved` when `singleLink` is true. */
  compactLabels?: string[];
}

function compactThresholdFragment(r: ResolvedDashboardSetting, shortLabel: string): string {
  const defaultTag = r.source === 'default' ? ' (default)' : '';
  return `${shortLabel} ${r.displayValue}${defaultTag}`;
}

/** Multiple threshold notes in one block (one line each), or one compact line with a single link. */
export function DashboardSettingSourceNotes({
  resolved,
  className = '',
  singleLink = false,
  compactLabels,
}: DashboardSettingSourceNotesProps) {
  if (resolved.length === 0) return null;

  if (singleLink) {
    const parts = resolved.map((r, i) =>
      compactThresholdFragment(r, compactLabels?.[i] ?? r.name)
    );
    const allFromSettings = resolved.every((r) => r.source === 'settings');
    const allDefault = resolved.every((r) => r.source === 'default');
    const sourceNote = allFromSettings
      ? ' (from Settings)'
      : allDefault
        ? ' (hard-coded defaults)'
        : '';
    return (
      <p className={`text-xs text-slate-500 ${className}`.trim()}>
        Severity thresholds: {parts.join(', ')}
        {sourceNote}.{' '}
        <Link to={ROUTES.settings} className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
          Change in Settings
        </Link>
      </p>
    );
  }

  return (
    <div className={`space-y-0.5 ${className}`.trim()}>
      {resolved.map((r) => (
        <DashboardSettingSourceNote key={r.name} resolved={r} />
      ))}
    </div>
  );
}
